import { get } from 'lodash';

import axios from 'axios';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { PAGE_SIZE } from 'services/constants/env';
import { notificationContainer } from 'utils/notificationContainer';
import {
	getErrorCorrectionsRequest,
	getErrorCorrectionsSuccess,
	getErrorCorrectionRequest,
	getErrorCorrectionSuccess,
	getErrorTypesRequest,
	getErrorTypesSuccess,
	getErrorCorrectionsScriptHistoryRequest,
	getErrorCorrectionsScriptHistorySuccess,
	updateCommentRequest,
	updateCommunicationWithClientRequest,
	updateProposedSolutionRequest,
	updateResponsibleUserRequest,
	updateStatusRequest,
	getScriptsRequest,
	getScriptsSuccess,
	startScriptRequest,
	startScriptSuccess,
	startScriptFailure,
	resetParameterUpdateCounter,
	updateParameterUpdateCounter,
	getUserStatisticRequest,
	getUserStatisticSuccess,
	getLaunchedScriptsRequest,
	getLaunchedScriptsSuccess,
	startScriptByOrderRequest,
	startScriptByOrderSuccess,
	startScriptByOrderFailure,
} from './reducer';
import {
	TErrorCorrectionsResponsePayload,
	TErrorCorrectionsRequest,
	TErrorCorrectionRequest,
	TErrorCorrectionResponsePayload,
	TErrorCorrectionsErrorTypesResponsePayload,
	TErrorCorrectionsScriptHistoryRequest,
	TErrorCorrectionsScriptHistoryResponsePayload,
	TErrorCorrectionsUpdateCommentRequest,
	TErrorCorrectionsUpdateCommunicationWithClientRequest,
	TErrorCorrectionsUpdateProposedSolutionRequest,
	TErrorCorrectionsUpdateResponsibleUserRequest,
	TErrorCorrectionsUpdateStatusRequest,
	TErrorCorrectionsScriptsResponsePayload,
	TErrorCorrectionsStartScriptRequest,
	TErrorCorrectionsStartScriptResponsePayload,
	TErrorCorrectionsUserStatisticResponsePayload,
	TLaunchedScriptsRequest,
	TLaunchedScriptsResponsePayload,
	TStartScriptByOrderRequest,
	TErrorCorrectionsStore,
} from './types';
import { getErrorCorrectionsState } from './selectors';

/**
 * A record's status is on screen in exactly two places: the open item and the list. Refetch
 * whichever of them actually holds this record, instead of reloading the whole application —
 * a reload also re-runs the Keycloak handshake and throws away the page the user was on.
 */
function* refreshUpdatedErrorCorrection(errorId: number) {
	const state: TErrorCorrectionsStore = yield select(getErrorCorrectionsState);

	if (state.errorCorrection && Number(state.errorCorrection.id) === errorId) {
		yield put(getErrorCorrectionRequest({ errorId }));
	}

	if (state.lastListRequest && state.errorCorrections) {
		yield put(getErrorCorrectionsRequest(state.lastListRequest));
	}
}

function* getErrorCorrectionsRequestWorker(action: PayloadAction<TErrorCorrectionsRequest>) {
	const { payload } = action;

	try {
		const response: TErrorCorrectionsResponsePayload = yield call(
			api.errorCorrections.getErrorCorrections,
			payload,
		);
		yield put(getErrorCorrectionsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		// yield put(assetsInitState());
	}
}

function* getErrorCorrectionRequestWorker(action: PayloadAction<TErrorCorrectionRequest>) {
	const { payload } = action;

	try {
		const response: TErrorCorrectionResponsePayload = yield call(
			api.errorCorrections.getErrorCorrection,
			payload,
		);
		yield put(getErrorCorrectionSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		// yield put(assetsInitState());
	}
}

export function* getErrorTypesRequestWorker() {
	try {
		const response: TErrorCorrectionsErrorTypesResponsePayload = yield call(
			api.errorCorrections.getErrorTypes,
		);
		yield put(getErrorTypesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		// yield put(assetsInitState());
	}
}

function* getErrorCorrectionsScriptHistoryRequestWorker(
	action: PayloadAction<TErrorCorrectionsScriptHistoryRequest>,
) {
	const { payload } = action;

	try {
		const response: TErrorCorrectionsScriptHistoryResponsePayload = yield call(
			api.errorCorrections.getErrorCorrectionsScriptHistory,
			payload,
		);
		yield put(getErrorCorrectionsScriptHistorySuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		// yield put(assetsInitState());
	}
}

function* updateCommentRequestWorker(action: PayloadAction<TErrorCorrectionsUpdateCommentRequest>) {
	const { payload } = action;

	try {
		yield call(api.errorCorrections.updateErrorCorrectionComment, payload);
		yield put(updateParameterUpdateCounter());
		// yield put(getErrorCorrectionRequest({ errorId: payload.errorId }));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* updateCommunicationWithClientRequestWorker(
	action: PayloadAction<TErrorCorrectionsUpdateCommunicationWithClientRequest>,
) {
	const { payload } = action;

	try {
		yield call(api.errorCorrections.updateErrorCorrectionCommunicationWithClient, payload);
		yield put(updateParameterUpdateCounter());

		// yield put(getErrorCorrectionRequest({ errorId: payload.errorId }));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* updateProposedSolutionRequestWorker(
	action: PayloadAction<TErrorCorrectionsUpdateProposedSolutionRequest>,
) {
	const { payload } = action;

	try {
		yield call(api.errorCorrections.updateErrorCorrectionProposedSolution, payload);
		yield put(updateParameterUpdateCounter());

		// yield put(getErrorCorrectionRequest({ errorId: payload.errorId }));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* updateResponsibleUserRequestWorker(
	action: PayloadAction<TErrorCorrectionsUpdateResponsibleUserRequest>,
) {
	const { payload } = action;
	const reload = get(payload, 'reload', true);

	try {
		yield call(api.errorCorrections.updateErrorCorrectionResponsibleUser, payload);
		yield put(updateParameterUpdateCounter());
		if (reload) yield call(refreshUpdatedErrorCorrection, payload.errorId);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* updateStatusRequestWorker(action: PayloadAction<TErrorCorrectionsUpdateStatusRequest>) {
	const { payload } = action;
	const reload = get(payload, 'reload', true);

	try {
		yield call(api.errorCorrections.updateErrorCorrectionStatus, payload);
		yield put(updateParameterUpdateCounter());
		if (reload) yield call(refreshUpdatedErrorCorrection, payload.errorId);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

export function* getScriptsRequestWorker() {
	try {
		const response: TErrorCorrectionsScriptsResponsePayload = yield call(
			api.errorCorrections.getScripts,
		);
		yield put(getScriptsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
	}
}

function* startScriptRequestWorker(action: PayloadAction<TErrorCorrectionsStartScriptRequest>) {
	const { payload } = action;

	try {
		const response: TErrorCorrectionsStartScriptResponsePayload = yield call(
			api.errorCorrections.startScript,
			payload,
		);
		yield put(startScriptSuccess(response));
		yield put(updateParameterUpdateCounter());
		console.log('====================================');
		console.log('startScriptRequestWorker', response);
		yield put(getErrorCorrectionRequest({ errorId: payload.errorId }));

		// window.location.reload();
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? 'Error'),
				'error',
			);
			yield put(
				startScriptFailure(
					String(error?.response?.data?.message ?? error?.response?.data ?? 'Error'),
				),
			);
		}
	}
}

export function* getUserStatisticRequestWorker() {
	try {
		const response: TErrorCorrectionsUserStatisticResponsePayload = yield call(
			api.errorCorrections.getUserStatistic,
		);
		yield put(getUserStatisticSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			String(error?.response?.data?.message ?? error?.response?.data ?? 'Error');
		}
	}
}

function* getLaunchedScriptsRequestWorker(action: PayloadAction<TLaunchedScriptsRequest>) {
	const { payload } = action;

	try {
		const response: TLaunchedScriptsResponsePayload = yield call(
			api.errorCorrections.getLaunchedScripts,
			payload,
		);
		yield put(getLaunchedScriptsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		yield put(getLaunchedScriptsSuccess(null));
	}
}

function* startScriptByOrderRequestWorker(action: PayloadAction<TStartScriptByOrderRequest>) {
	const { payload } = action;

	try {
		const response: TErrorCorrectionsStartScriptResponsePayload = yield call(
			api.errorCorrections.startScriptByOrder,
			payload,
		);
		notificationContainer('Script launched successfully', 'success');
		yield put(startScriptByOrderSuccess(response));
		yield put(getLaunchedScriptsRequest({ orderId: payload.id }));
		yield put(updateParameterUpdateCounter());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? 'Error'),
				'error',
			);
			yield put(
				startScriptByOrderFailure(
					String(error?.response?.data?.message ?? error?.response?.data ?? 'Error'),
				),
			);
		}
	}
}

export function* errorCorrectionsSaga() {
	yield takeEvery(getErrorCorrectionsRequest.type, getErrorCorrectionsRequestWorker);
	yield takeEvery(getErrorCorrectionRequest.type, getErrorCorrectionRequestWorker);
	yield takeEvery(getErrorTypesRequest.type, getErrorTypesRequestWorker);
	yield takeEvery(
		getErrorCorrectionsScriptHistoryRequest.type,
		getErrorCorrectionsScriptHistoryRequestWorker,
	);
	yield takeEvery(updateCommentRequest.type, updateCommentRequestWorker);
	yield takeEvery(
		updateCommunicationWithClientRequest.type,
		updateCommunicationWithClientRequestWorker,
	);
	yield takeEvery(updateProposedSolutionRequest.type, updateProposedSolutionRequestWorker);
	yield takeEvery(updateResponsibleUserRequest.type, updateResponsibleUserRequestWorker);
	yield takeEvery(updateStatusRequest.type, updateStatusRequestWorker);
	yield takeEvery(getScriptsRequest.type, getScriptsRequestWorker);
	yield takeEvery(startScriptRequest.type, startScriptRequestWorker);
	yield takeEvery(getUserStatisticRequest.type, getUserStatisticRequestWorker);
	yield takeEvery(getLaunchedScriptsRequest.type, getLaunchedScriptsRequestWorker);
	yield takeEvery(startScriptByOrderRequest.type, startScriptByOrderRequestWorker);
}
